import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import { usePersona } from '@/app/providers';
import type { Capability, Persona, Role } from '@/shared/types';
import type { Plant } from '@/versions/v1/schema/plant';
import { PLANT } from '@/versions/v1/shared/bunker-hill-seed';

// =========================================================
// Role context
// =========================================================
// Layered on top of `usePersona()` so that visibility-gating reads from the
// active *role* and its capabilities, not from the persona display name. The
// persona-to-role mapping is a sensible default for the mock; a real build
// would let an admin override it per user. The mapping lives here in one
// place so the rest of the codebase only ever asks `can(...)`.
//
// OptiFlow domain capabilities (case, fmea, alarm, …) grant per plant persona
// per docs/ideas/tim-scope/roles-and-capabilities.md §1.1; foundation content
// capabilities still aggregate on Role.
//
// Spec: docs/logic/workspace/roles-and-capabilities.md

// Default persona-to-role mapping. Returning a Role for every Persona value
// keeps the call sites total — no branch ever sees `undefined`.
export function defaultRoleForPersona(persona: Persona): Role {
  switch (persona) {
    case 'Admin':
    case 'GM':
    case 'Superintendent':
      return 'admin';
    case 'Editor':
    case 'Metallurgist':
    case 'Senior metallurgist':
    case 'Ops supervisor':
    case 'Maintenance':
      return 'editor';
    case 'Reviewer':
      return 'reviewer';
    case 'Viewer':
    case 'Control room':
    case 'Operator':
      return 'viewer';
  }
}

// Foundation content capabilities per role. Naming convention: `domain:verb`.
export const CAPABILITIES_BY_ROLE: Record<Role, ReadonlySet<Capability>> = {
  admin: new Set<Capability>([
    'content:write',
    'content:review',
    'roles:admin',
    'see:all',
  ]),
  editor: new Set<Capability>(['content:write', 'see:all']),
  reviewer: new Set<Capability>(['content:review', 'see:all']),
  // Viewers are read-only; they see everything but change nothing.
  viewer: new Set<Capability>(['see:all']),
};

/** OptiFlow plant-persona domain grants — reconcile.T3 / roles-and-capabilities §1.1. */
export const DOMAIN_CAPABILITIES_BY_PERSONA: Partial<
  Record<Persona, ReadonlySet<Capability>>
> = {
  Admin: new Set<Capability>(['money:view']),
  GM: new Set<Capability>([
    'kpi:view-exec',
    'escalation:decide',
    'fmea:approve',
    'target:set',
    'case:view',
    'claim:rule',
    'model:accept',
    'money:view',
  ]),
  'Senior metallurgist': new Set<Capability>([
    'fmea:review',
    'fmea:approve',
    'case:view',
    'case:diagnose',
  ]),
  Superintendent: new Set<Capability>(['kpi:view-exec', 'case:view', 'wo:view', 'escalation:decide']),
  Metallurgist: new Set<Capability>([
    'case:diagnose',
    'action:approve',
    'fmea:review',
    'sample:register',
    'case:view',
    'case:act',
    'claim:rule',
    'model:accept',
  ]),
  'Ops supervisor': new Set<Capability>([
    'case:act',
    'alarm:ack',
    'sample:register',
    'case:view',
  ]),
  'Control room': new Set<Capability>([
    'case:evidence',
    'case:act',
    'action:approve',
    'alarm:ack',
    'case:view',
  ]),
  Operator: new Set<Capability>([
    'sample:collect',
    'case:evidence',
    'case:act',
    'case:view',
  ]),
  Maintenance: new Set<Capability>([
    'wo:assign',
    'wo:view',
    'fmea:review',
    'action:approve',
    'case:view',
  ]),
};

export function domainCapabilitiesForPersona(
  persona: Persona,
): ReadonlySet<Capability> {
  return DOMAIN_CAPABILITIES_BY_PERSONA[persona] ?? new Set();
}

/**
 * D19: the seat that approves a living-FMEA proposal. A site whose senior
 * metallurgist seat is folded into the manager (`seniorMetallurgist:
 * 'absorbed-by-manager'`, Bunker Hill) seats the GM there; otherwise the
 * Senior metallurgist persona holds it.
 */
export function seniorMetallurgistSeatHolder(
  plant: Pick<Plant, 'seniorMetallurgist'> = PLANT,
): Persona {
  return plant.seniorMetallurgist === 'absorbed-by-manager' ? 'GM' : 'Senior metallurgist';
}

/** Capabilities a persona holds by the seat it sits in at this site. */
export function seatCapabilitiesForPersona(
  persona: Persona,
  plant: Pick<Plant, 'seniorMetallurgist'> = PLANT,
): ReadonlySet<Capability> {
  return persona === seniorMetallurgistSeatHolder(plant)
    ? new Set<Capability>(['fmea:approve-proposal'])
    : new Set<Capability>();
}

export function personaCan(
  persona: Persona,
  capability: Capability,
  plant: Pick<Plant, 'seniorMetallurgist'> = PLANT,
): boolean {
  const role = defaultRoleForPersona(persona);
  if (CAPABILITIES_BY_ROLE[role].has(capability)) return true;
  if (domainCapabilitiesForPersona(persona).has(capability)) return true;
  return seatCapabilitiesForPersona(persona, plant).has(capability);
}

// Human-readable role label — used by the persona dropdown subtitle in the
// TopBar so the user sees both who they are and what they can do.
export function roleLabel(role: Role): string {
  switch (role) {
    case 'admin':
      return 'Admin';
    case 'editor':
      return 'Editor';
    case 'reviewer':
      return 'Reviewer';
    case 'viewer':
      return 'Viewer (read-only)';
  }
}

type RoleContextValue = {
  role: Role;
  can: (capability: Capability) => boolean;
};

const RoleContext = createContext<RoleContextValue | null>(null);

// RoleProvider — derives the active role from the active persona via the
// default mapping above. Mounted by the version entry shell.
export function RoleProvider({ children }: { children: ReactNode }) {
  const { persona } = usePersona();
  const role = useMemo(() => defaultRoleForPersona(persona), [persona]);

  const can = useCallback(
    (capability: Capability) => personaCan(persona, capability),
    [persona],
  );

  const value = useMemo<RoleContextValue>(() => ({ role, can }), [role, can]);

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}

export function useRole(): RoleContextValue {
  const ctx = useContext(RoleContext);
  if (!ctx) {
    // Safe fallback — if a scene mounts outside the RoleProvider (legacy or
    // test harness), default to viewer (least-privileged) so capability
    // checks fail safe. The provider should always be in the tree for normal
    // app boots.
    return {
      role: 'viewer',
      can: (capability: Capability) =>
        CAPABILITIES_BY_ROLE['viewer'].has(capability),
    };
  }
  return ctx;
}
